import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxmUserauthV1RoleQueryServiceRoleGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-role-query-service-role-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1RoleState } from '@interxfi/types';
import { UserauthRoleEntityMenu } from '../../../../../../features/userauth/role/userauth-role-entity-menu';
import { UserauthRoleEventSummary } from '../../../../../../features/userauth/role/userauth-role-event';
import { getUserauthRoleStateListItems } from '../../../../../../features/userauth/role/userauth-role-list-items';
import { getUserauthRoleDetailPath } from '../../../../../../features/userauth/role/userauth-role-routing';
import { UserauthRoleStatusPill } from '../../../../../../features/userauth/role/userauth-role-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/role/_role/$roleId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `role-${params.roleId}`,
        label: 'Role',
        to: getUserauthRoleDetailPath(params.roleId),
        suffix: <CopyButton content={params.roleId} label="Copy Role ID" tooltip={{ copy: params.roleId, copied: 'Copied Role ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Role'),
  component: RoleDetail,
});

function getListItems(data: IxmUserauthV1RoleState | undefined) {
  const items = getUserauthRoleStateListItems(data);

  return {
    left: [items.data.spec.label, items.data.spec.scopes] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.roleId, items.organizationId] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load role', 'view this role');

function RoleDetail() {
  const { roleId } = Route.useParams();
  const request = { roleId } as const;
  const { data, error, isPending } = useIxmUserauthV1RoleQueryServiceRoleGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.role), [data?.role]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <UserauthRoleStatusPill isPending={showPending} size="large" status={data?.role?.status} />
          </HorizontalStack>

          <UserauthRoleEntityMenu roleId={roleId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <UserauthRoleEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
